import api from './api';

const billingService = {
  // ─── Subscription ──────────────────────────────────────────
  getSubscription: async (organizationId) => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/billing/subscription', { params });
    return response.data.data;
  },

  getEffectiveLimits: async (organizationId) => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/billing/limits', { params });
    return response.data.data;
  },

  getPlanCatalog: async () => {
    const response = await api.get('/billing/plans');
    return response.data.data;
  },

  getPublicPlanCatalog: async () => {
    const response = await api.get('/billing/public/plans');
    return response.data.data;
  },

  // ─── Checkout & Plan Changes ───────────────────────────────
  createCheckoutSession: async ({ organizationId, plan, billingCycle = 'monthly', seats = 1, couponCode }) => {
    const response = await api.post('/billing/checkout', {
      organizationId,
      plan,
      billingCycle,
      seats,
      couponCode,
    });
    return response.data.data;
  },

  confirmCheckout: async ({ organizationId, plan, billingCycle = 'monthly', seats = 1, razorpayPaymentId, razorpaySubscriptionId, razorpaySignature }) => {
    const response = await api.post('/billing/checkout/confirm', {
      organizationId,
      plan,
      billingCycle,
      seats,
      razorpayPaymentId,
      razorpaySubscriptionId,
      razorpaySignature,
    });
    return response.data.data;
  },

  startTrial: async ({ plan, organizationId, seats, couponCode } = {}) => {
    const response = await api.post('/billing/trial', { plan, organizationId, seats, couponCode });
    return response.data.data;
  },

  changePlan: async ({ subscriptionId, newPlan, newBillingCycle }) => {
    const response = await api.put('/billing/plan', { subscriptionId, newPlan, newBillingCycle });
    return response.data.data;
  },

  cancelPendingPlanChange: async ({ subscriptionId }) => {
    const response = await api.post('/billing/plan/cancel-pending', { subscriptionId });
    return response.data.data;
  },

  // ─── Seats ─────────────────────────────────────────────────
  updateSeats: async ({ subscriptionId, newSeatCount }) => {
    const response = await api.put('/billing/seats', { subscriptionId, newSeatCount });
    return response.data.data;
  },

  cancelPendingSeatChange: async ({ subscriptionId }) => {
    const response = await api.post('/billing/seats/cancel-pending', { subscriptionId });
    return response.data.data;
  },

  getSeatUsage: async (organizationId) => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/billing/seats', { params });
    return response.data.data;
  },

  // ─── Payment & Portal ─────────────────────────────────────
  updatePaymentMethod: async ({ subscriptionId }) => {
    const response = await api.post('/billing/payment-method', { subscriptionId });
    return response.data.data;
  },

  createPortalSession: async ({ subscriptionId }) => {
    const response = await api.post('/billing/portal', { subscriptionId });
    return response.data.data;
  },

  // ─── Cancellation ─────────────────────────────────────────
  cancelSubscription: async ({ subscriptionId, reason, immediate = false } = {}) => {
    const response = await api.post('/billing/cancel', { subscriptionId, reason, immediate });
    return response.data.data;
  },

  reactivateSubscription: async ({ subscriptionId }) => {
    const response = await api.post('/billing/reactivate', { subscriptionId });
    return response.data.data;
  },

  // ─── Invoices ──────────────────────────────────────────────
  getInvoices: async (organizationId) => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/billing/invoices', { params });
    return response.data.data;
  },

  downloadInvoiceDocument: async ({ invoiceId, type = 'invoice', organizationId }) => {
    const params = { type };
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get(`/billing/invoices/${invoiceId}/document`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  previewInvoiceDocument: async ({ invoiceId, type = 'invoice', organizationId }) => {
    const params = { type, format: 'html' };
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get(`/billing/invoices/${invoiceId}/document`, {
      params,
      responseType: 'text',
    });
    return response.data;
  },

  // ─── Coupons ───────────────────────────────────────────────
  // Backend expects { code, plan?, seats?, organizationId? } and returns
  // { valid, reason?, coupon?: {...}, discount?: { amount, currency, description } }
  validateCoupon: async (code, opts = {}) => {
    const payload = { code };
    if (opts.plan) payload.plan = opts.plan;
    if (opts.seats != null) payload.seats = opts.seats;
    if (opts.organizationId) payload.organizationId = opts.organizationId;
    if (opts.priceRegion) payload.priceRegion = opts.priceRegion;
    const response = await api.post('/coupons/validate', payload);
    return response.data.data;
  },

  // ─── Org Members (for seat management) ─────────────────────
  getOrgMembers: async (organizationId, { departmentId, search } = {}) => {
    const params = { organizationId };
    if (departmentId && departmentId !== 'all') params.departmentId = departmentId;
    if (search && search.trim()) params.search = search.trim();
    const response = await api.get('/billing/members', { params });
    return response.data.data;
  },

  // ─── Usage ─────────────────────────────────────────────────
  getUsageSummary: async (organizationId) => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/usage', { params });
    return response.data.data;
  },

  getItemUsage: async (itemId, organizationId) => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get(`/usage/${itemId}`, { params });
    return response.data.data;
  },
};

export default billingService;
